import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronRight,
  Loader2,
  AlertCircle,
  Info,
  Store,
  Package,
  CreditCard,
  Receipt,
  StickyNote,
} from "lucide-react";
import ProductPicker from "./ProductPicker";
import SupplierPicker from "./SupplierPicker";
import AdminSellerEditModal from "../AdminSeller/AdminSellerEditModal";
import {
  API,
  STATUSES,
  PAYMENT_STATUSES,
  PAYMENT_METHODS,
  num,
  round2,
  fmtMoney,
  toDateInput,
  calcTotals,
  errMsg,
  purchaseNumber,
  productNameOf,
} from "./Purchaseshared";

/* ------------------------------------------------------------------ */
/*  small UI pieces                                                    */
/* ------------------------------------------------------------------ */
const cardCls = "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";
const inputCls = (bad) =>
  `w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 ${bad ? "border-red-400 focus:ring-red-200" : "border-slate-300 focus:ring-[#d4af37]/50 focus:border-transparent"
  }`;

const Card = ({ title, icon: Icon, children, right }) => (
  <section className={cardCls}>
    <header className="flex items-center justify-between gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-200 rounded-t-xl">
      <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-800">
        {Icon && <Icon size={16} className="text-slate-500" />}
        {title}
      </h2>
      {right}
    </header>
    {children}
  </section>
);

const Field = ({ label, required, error, hint, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error ? (
      <p className="mt-1 text-xs text-red-600" role="alert">
        {error}
      </p>
    ) : (
      hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>
    )}
  </div>
);

const STATUS_HINT = {
  Pending: "Ordered from the vendor. No stock is added yet.",
  Received: "Goods arrived. Stock of catalogue items is added to inventory.",
  Cancelled: "Cancelled. No stock is added.",
};

let rowSeq = 0;
const newRow = (over = {}) => ({
  key: `row-${(rowSeq += 1)}`,
  product: "",
  name: "",
  sku: "",
  isManual: false,
  quantity: "1",
  unitCost: "",
  ...over,
});

/* ------------------------------------------------------------------ */
/*  one item row                                                       */
/* ------------------------------------------------------------------ */
const ItemRow = ({ item, index, products, setProducts, takenIds, errors = {}, onChange, onRemove, canRemove }) => {
  const amount = round2(num(item.quantity) * num(item.unitCost));
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Item {index + 1}</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={`Remove item ${index + 1}`}
          className="p-1.5 rounded-md text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <ProductPicker
        products={products}
        setProducts={setProducts}
        value={item}
        takenIds={takenIds}
        invalid={Boolean(errors.product)}
        onChange={(patch) => onChange(patch)}
      />
      {errors.product && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {errors.product}
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1" htmlFor={`${item.key}-qty`}>
            Quantity
          </label>
          <input
            id={`${item.key}-qty`}
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={item.quantity}
            onChange={(e) => onChange({ quantity: e.target.value })}
            className={inputCls(errors.quantity)}
          />
          {errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1" htmlFor={`${item.key}-cost`}>
            Unit cost (₹)
          </label>
          <input
            id={`${item.key}-cost`}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={item.unitCost}
            onChange={(e) => onChange({ unitCost: e.target.value })}
            placeholder="0.00"
            className={inputCls(errors.unitCost)}
          />
          {errors.unitCost && <p className="mt-1 text-xs text-red-600">{errors.unitCost}</p>}
        </div>
        <div className="col-span-2 md:col-span-1">
          <span className="block text-xs text-slate-500 mb-1">Amount</span>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-white">
            {fmtMoney(amount)}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  page                                                               */
/* ------------------------------------------------------------------ */
const AdminPurchaseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isAddVendorOpen, setIsAddVendorOpen] = useState(false);
  const [addVendorInitialName, setAddVendorInitialName] = useState("");

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [original, setOriginal] = useState(null); // purchase as loaded (edit mode)

  const [form, setForm] = useState({
    supplier: "",
    supplierName: "",
    isManualSupplier: false,
    purchaseDate: toDateInput(),
    supplierInvoiceNo: "",
    status: "Pending",
    paymentStatus: "Unpaid",
    amountPaid: "",
    paymentMethod: "Bank Transfer",
    gstPercent: "",
    shippingAmount: "",
    discountAmount: "",
    notes: "",
  });
  const [items, setItems] = useState(() => [newRow()]);
  const topRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  /* ---------- load ---------- */
  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [supRes, prodRes] = await Promise.all([
        axios.get(`${API}/supplier/suppliers`),
        axios.get(`${API}/products/get-all-products`),
      ]);
      setSuppliers(supRes.data.suppliers || supRes.data.data || []);
      setProducts(prodRes.data.products || []);

      if (isEdit) {
        const { data } = await axios.get(`${API}/purchases/${id}`);
        const p = data.purchase;
        setOriginal(p);
        setForm({
          supplier: p.supplier?._id || p.supplier || "",
          supplierName: p.supplierName || "",
          isManualSupplier: !p.supplier,
          purchaseDate: toDateInput(p.purchaseDate || p.createdAt),
          supplierInvoiceNo: p.supplierInvoiceNo || "",
          status: p.status,
          paymentStatus: p.paymentStatus,
          amountPaid: p.paymentStatus === "Partially Paid" ? String(p.amountPaid || "") : "",
          paymentMethod: p.paymentMethod || "Bank Transfer",
          gstPercent: p.gstPercent ? String(p.gstPercent) : "",
          shippingAmount: p.shippingAmount ? String(p.shippingAmount) : "",
          discountAmount: p.discountAmount ? String(p.discountAmount) : "",
          notes: p.notes || "",
        });
        setItems(
          (p.products || []).map((it) => {
            const hasProduct = Boolean(it.product);
            return newRow({
              product: hasProduct ? it.product._id || it.product : "",
              name: hasProduct ? productNameOf(it.product) || it.name || "" : it.name || "",
              sku: hasProduct ? it.product.sku || it.sku || "" : it.sku || "",
              isManual: !hasProduct,
              quantity: String(it.quantity),
              unitCost: String(it.unitCost),
            });
          })
        );
      }
    } catch (err) {
      console.error(err);
      setLoadError(errMsg(err, "Failed to load the data for this form."));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ---------- derived ---------- */
  const totals = useMemo(() => calcTotals({ items, ...form }), [items, form]);

  const takenIds = useMemo(() => new Set(items.map((i) => i.product).filter(Boolean)), [items]);

  const paidNow = useMemo(() => {
    if (form.paymentStatus === "Paid") return totals.total;
    if (form.paymentStatus === "Partially Paid") return round2(num(form.amountPaid));
    return 0;
  }, [form.paymentStatus, form.amountPaid, totals.total]);

  const wasReceived = isEdit && original?.status === "Received";

  /* ---------- validation ---------- */
  const errors = useMemo(() => {
    const e = { items: items.map(() => ({})) };
    if (form.isManualSupplier) {
      if (!form.supplierName.trim()) e.supplier = "Enter a vendor name";
    } else if (!form.supplier) {
      e.supplier = "Select a vendor, or enter manually";
    }

    const seen = new Set();
    items.forEach((it, i) => {
      const r = e.items[i];
      if (it.isManual) {
        if (!it.name.trim()) r.product = "Enter a name for this item";
      } else if (!it.product) {
        r.product = "Choose a product, or enter the item manually";
      } else if (seen.has(it.product)) {
        r.product = "This product is already added in another row";
      } else {
        seen.add(it.product);
      }
      const q = Number(it.quantity);
      if (!Number.isInteger(q) || q < 1) r.quantity = "Whole number, at least 1";
      if (it.unitCost === "" || !(Number(it.unitCost) >= 0)) r.unitCost = "Enter the cost";
    });

    if (form.gstPercent !== "" && !(Number(form.gstPercent) >= 0 && Number(form.gstPercent) <= 100))
      e.gstPercent = "Between 0 and 100";
    if (form.shippingAmount !== "" && !(Number(form.shippingAmount) >= 0)) e.shippingAmount = "0 or more";
    if (form.discountAmount !== "" && !(Number(form.discountAmount) >= 0)) e.discountAmount = "0 or more";
    else if (totals.discount > totals.subTotal) e.discountAmount = "More than the items subtotal";

    if (form.paymentStatus === "Partially Paid") {
      const paid = num(form.amountPaid);
      if (!(paid > 0 && paid < totals.total)) e.amountPaid = "More than 0 and less than the total";
    }
    return e;
  }, [form, items, totals]);

  const hasErrors =
    Boolean(errors.supplier || errors.gstPercent || errors.shippingAmount || errors.discountAmount || errors.amountPaid) ||
    errors.items.some((r) => Object.keys(r).length > 0);

  const show = (v) => (submitted ? v : undefined);

  /* ---------- item handlers ---------- */
  const updateItem = (key, patch) => setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addItem = () => setItems((rows) => [...rows, newRow()]);
  const removeItem = (key) => setItems((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));

  /* ---------- save ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) {
      toast.error("Please fix the highlighted fields");
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const payload = {
      supplier: form.isManualSupplier ? null : form.supplier,
      supplierName: form.isManualSupplier ? form.supplierName.trim() : "",
      purchaseDate: form.purchaseDate,
      supplierInvoiceNo: form.supplierInvoiceNo.trim(),
      status: form.status,
      paymentStatus: form.paymentStatus,
      amountPaid: form.paymentStatus === "Partially Paid" ? num(form.amountPaid) : 0,
      paymentMethod: form.paymentMethod,
      gstPercent: num(form.gstPercent),
      shippingAmount: num(form.shippingAmount),
      discountAmount: num(form.discountAmount),
      notes: form.notes.trim(),
      products: items.map((it) => ({
        product: it.isManual ? null : it.product,
        name: it.name.trim(),
        sku: it.sku.trim(),
        quantity: Number(it.quantity),
        unitCost: Number(it.unitCost),
      })),
    };

    setSaving(true);
    try {
      const res = isEdit
        ? await axios.put(`${API}/purchases/${id}`, payload)
        : await axios.post(`${API}/purchases/create`, payload);
      toast.success(isEdit ? "Purchase updated" : "Purchase created");
      navigate(`/admin-purchase/view/${res.data.purchase?._id || id}`);
    } catch (err) {
      console.error(err);
      toast.error(errMsg(err, "Failed to save purchase"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- states ---------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F6FB] p-6 animate-pulse">
        <div className="max-w-[1280px] mx-auto space-y-5">
          <div className="h-4 w-56 bg-slate-200 rounded" />
          <div className="grid lg:grid-cols-[1fr_360px] gap-5">
            <div className="h-[520px] bg-white border border-slate-200 rounded-xl" />
            <div className="h-[420px] bg-white border border-slate-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F3F6FB] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle size={44} className="text-slate-300" />
        <p className="mt-3 text-lg font-semibold text-slate-800">Couldn't open the form</p>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">{loadError}</p>
        <div className="flex gap-3 mt-5">
          <button onClick={loadData} className="px-4 py-2 rounded-lg text-sm font-medium bg-[#b07d17] text-white hover:bg-[#8c6312]">
            Try again
          </button>
          <button
            onClick={() => navigate("/admin-purchase")}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-300 hover:bg-slate-50"
          >
            Back to purchases
          </button>
        </div>
      </div>
    );
  }

  const manualCount = items.filter((i) => i.isManual).length;

  return (
    <div className="min-h-screen bg-[#F3F6FB] p-4 md:p-6 font-sans">
      <form id="purchase-form" onSubmit={handleSubmit} noValidate className="w-full mx-auto space-y-3">
        <div ref={topRef} />

        {/* Breadcrumb + title */}
        <nav className="flex items-center gap-1 text-sm text-slate-500" aria-label="Breadcrumb">
          <button
            type="button"
            onClick={() => navigate("/admin-purchase")}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
          >
            <ArrowLeft size={15} /> Purchases
          </button>
          <ChevronRight size={14} />
          <span className="text-slate-700 font-medium">{isEdit ? `Edit ${purchaseNumber(original)}` : "New purchase"}</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-[28px] leading-8 font-extrabold text-white tracking-tight">
              {isEdit ? "Edit purchase" : "Create purchase"}
            </h1>
            {/* <p className="text-sm text-slate-500 mt-1.5">Record goods bought from a vendor. Totals are calculated for you.</p> */}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm disabled:opacity-60"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create purchase"}
            </button>
          </div>
        </div>

        {wasReceived && (
          <div className="flex gap-2.5 rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-800">
            <Info size={16} className="mt-0.5 shrink-0" />
            <p>
              This purchase is already <b>Received</b>. Saving changes adjusts inventory by the <b>difference</b> only
              (for example, quantity 10 → 12 adds 2 more units).
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 items-start">
          {/* ---------------- LEFT ---------------- */}
          <div className="space-y-5 min-w-0">
            <Card title="Purchase details" icon={Store}>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Field label="Vendor / supplier" required error={show(errors.supplier)}>
                    <SupplierPicker
                      suppliers={suppliers}
                      value={{
                        supplier: form.supplier,
                        name: form.supplierName,
                        isManual: form.isManualSupplier,
                        onAddClick: (name) => {
                          setAddVendorInitialName(name);
                          setIsAddVendorOpen(true);
                        }
                      }}
                      onChange={(patch) => {
                        set({
                          supplier: patch.supplier ?? form.supplier,
                          supplierName: patch.name ?? form.supplierName,
                          isManualSupplier: patch.isManual ?? form.isManualSupplier,
                        });
                      }}
                      invalid={Boolean(show(errors.supplier))}
                    />
                  </Field>
                </div>
                <Field label="Purchase date" htmlFor="purchaseDate">
                  <input
                    id="purchaseDate"
                    type="date"
                    value={form.purchaseDate}
                    onChange={(e) => set({ purchaseDate: e.target.value })}
                    className={inputCls(false)}
                  />
                </Field>
                <Field label="Vendor invoice no." hint="Optional. The number printed on the vendor's own bill." htmlFor="inv">
                  <input
                    id="inv"
                    value={form.supplierInvoiceNo}
                    onChange={(e) => set({ supplierInvoiceNo: e.target.value })}
                    maxLength={80}
                    placeholder="e.g. INV/2026/0412"
                    className={inputCls(false)}
                  />
                </Field>
              </div>
            </Card>

            <Card
              title={`Items (${items.length})`}
              icon={Package}
              right={
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  <Plus size={16} /> Add item
                </button>
              }
            >
              <div className="p-5 space-y-4">
                {items.map((it, i) => (
                  <ItemRow
                    key={it.key}
                    item={it}
                    index={i}
                    products={products}
                    setProducts={setProducts}
                    takenIds={new Set([...takenIds].filter((pid) => pid !== it.product))}
                    errors={show(errors.items[i])}
                    onChange={(patch) => updateItem(it.key, patch)}
                    onRemove={() => removeItem(it.key)}
                    canRemove={items.length > 1}
                  />
                ))}

                <button
                  type="button"
                  onClick={addItem}
                  className="w-full rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm font-medium text-slate-600 hover:border-transparent hover:text-white hover:bg-amber-50/40 transition-colors"
                >
                  <Plus size={16} className="inline -mt-0.5 mr-1" /> Add another item
                </button>


              </div>
            </Card>

            <Card title="Notes" icon={StickyNote}>
              <div className="p-5">
                <label htmlFor="notes" className="sr-only">
                  Notes
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => set({ notes: e.target.value })}
                  placeholder="Anything worth remembering about this purchase (delivery details, quality remarks)…"
                  className={inputCls(false)}
                />
              </div>
            </Card>
          </div>

          {/* ---------------- RIGHT ---------------- */}
          <aside className="space-y-5 lg:sticky lg:top-6">
            <Card title="Status & payment" icon={CreditCard}>
              <div className="p-5 space-y-4">
                <Field label="Status" htmlFor="status" hint={STATUS_HINT[form.status]}>
                  <select id="status" value={form.status} onChange={(e) => set({ status: e.target.value })} className={inputCls(false)}>
                    {STATUSES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Payment status" htmlFor="payStatus">
                  <select
                    id="payStatus"
                    value={form.paymentStatus}
                    onChange={(e) => set({ paymentStatus: e.target.value })}
                    className={inputCls(false)}
                  >
                    {PAYMENT_STATUSES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                {form.paymentStatus === "Partially Paid" && (
                  <Field label="Amount paid so far (₹)" required error={show(errors.amountPaid)} htmlFor="amountPaid">
                    <input
                      id="amountPaid"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.amountPaid}
                      onChange={(e) => set({ amountPaid: e.target.value })}
                      className={inputCls(show(errors.amountPaid))}
                    />
                  </Field>
                )}

                <Field label="Payment method" htmlFor="payMethod">
                  <select
                    id="payMethod"
                    value={form.paymentMethod}
                    onChange={(e) => set({ paymentMethod: e.target.value })}
                    className={inputCls(false)}
                  >
                    {[...new Set([...PAYMENT_METHODS, form.paymentMethod])].map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </Card>

            <Card title="Charges" icon={Receipt}>
              <div className="p-5 grid grid-cols-2 gap-4">
                <Field label="GST (%)" error={show(errors.gstPercent)} htmlFor="gst">
                  <input
                    id="gst"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.gstPercent}
                    onChange={(e) => set({ gstPercent: e.target.value })}
                    placeholder="0"
                    className={inputCls(show(errors.gstPercent))}
                  />
                </Field>
                <Field label="Discount (₹)" error={show(errors.discountAmount)} htmlFor="disc">
                  <input
                    id="disc"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discountAmount}
                    onChange={(e) => set({ discountAmount: e.target.value })}
                    placeholder="0.00"
                    className={inputCls(show(errors.discountAmount))}
                  />
                </Field>
                <div className="col-span-2">
                  <Field label="Shipping / other charges (₹)" error={show(errors.shippingAmount)} htmlFor="ship">
                    <input
                      id="ship"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.shippingAmount}
                      onChange={(e) => set({ shippingAmount: e.target.value })}
                      placeholder="0.00"
                      className={inputCls(show(errors.shippingAmount))}
                    />
                  </Field>
                </div>
              </div>
            </Card>

            <section className={cardCls}>
              <div className="p-5">
                <h2 className="text-[15px] font-bold text-slate-800 mb-3">Order summary</h2>
                <dl className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Items subtotal</dt>
                    <dd className="text-slate-800">{fmtMoney(totals.subTotal)}</dd>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Discount</dt>
                      <dd className="text-slate-800">− {fmtMoney(totals.discount)}</dd>
                    </div>
                  )}
                  {num(form.gstPercent) > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">GST ({num(form.gstPercent)}%)</dt>
                      <dd className="text-slate-800">{fmtMoney(totals.tax)}</dd>
                    </div>
                  )}
                  {totals.shipping > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Shipping / other</dt>
                      <dd className="text-slate-800">{fmtMoney(totals.shipping)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 mt-1 border-t border-slate-200 text-base font-bold text-white">
                    <dt>Grand total</dt>
                    <dd>{fmtMoney(totals.total)}</dd>
                  </div>
                  {form.paymentStatus !== "Unpaid" && (
                    <>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Paid</dt>
                        <dd className="text-slate-800">{fmtMoney(paidNow)}</dd>
                      </div>
                      <div className="flex justify-between font-medium">
                        <dt className="text-slate-500">Balance due</dt>
                        <dd className={totals.total - paidNow > 0 ? "text-red-600" : "text-emerald-600"}>
                          {fmtMoney(Math.max(0, totals.total - paidNow))}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>

                {submitted && hasErrors && (
                  <p className="flex gap-2 mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" /> Some fields need your attention.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm disabled:opacity-60"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {saving ? "Saving…" : isEdit ? "Save changes" : "Create purchase"}
                </button>
              </div>
            </section>
          </aside>
        </div>
      </form>

      <AdminSellerEditModal
        isOpen={isAddVendorOpen}
        onClose={() => setIsAddVendorOpen(false)}
        seller={null} // Add mode
        initialName={addVendorInitialName}
        onSuccess={(newSupplier) => {
          if (newSupplier) {
            setSuppliers(prev => [newSupplier, ...prev]);
            set({ supplier: newSupplier._id, supplierName: newSupplier.businessName || newSupplier.fullName, isManualSupplier: false });
          }
        }}
      />
    </div>
  );
};

export default AdminPurchaseForm;